import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import "./OnboardingAdmin.css";


const API =
  "/api/onboarding-packs";

const AGREEMENT_API =
  "/api/agreements";


function authHeaders(
  json = true
) {
  const headers = {};

  if (json) {
    headers[
      "Content-Type"
    ] = "application/json";
  }

  const token =
    localStorage.getItem(
      "access_token"
    )
    ||
    localStorage.getItem(
      "token"
    )
    ||
    localStorage.getItem(
      "auth_token"
    );

  if (token) {
    headers.Authorization =
      `Bearer ${token}`;
  }

  return headers;
}


async function fetchJson(
  url,
  options = {}
) {
  const response = await fetch(
    url,
    {
      credentials: "include",
      ...options,

      headers: {
        ...authHeaders(
          !(
            options.body
            instanceof FormData
          )
        ),

        ...(
          options.headers
          || {}
        ),
      },
    }
  );

  let payload = null;

  try {
    payload =
      await response.json();
  } catch {
    payload = null;
  }

  if (!response.ok) {

    let message =
      payload?.detail?.message
      ||
      payload?.detail
      ||
      payload?.message
      ||
      `HTTP ${response.status}`;

    if (
      typeof message
      !== "string"
    ) {
      message =
        JSON.stringify(
          message
        );
    }

    throw new Error(
      message
    );
  }

  return payload;
}


async function onboardingApi(
  path,
  options = {}
) {
  return fetchJson(
    `${API}${path}`,
    options
  );
}


function Badge({
  status,
}) {
  const value =
    String(
      status
      || "unknown"
    ).toLowerCase();

  return (
    <span
      className={
        `rx-onb-admin-badge `
        + `rx-onb-admin-badge--${value}`
      }
    >
      {
        value
        .replaceAll(
          "_",
          " "
        )
        .toUpperCase()
      }
    </span>
  );
}


function Progress({
  value,
}) {
  const number = Math.max(
    0,
    Math.min(
      100,
      Number(
        value
        || 0
      )
    )
  );

  return (
    <div className="rx-onb-admin-progress">
      <div
        className="rx-onb-admin-progress__fill"
        style={{
          width: `${number}%`,
        }}
      />

      <span>
        {Math.round(number)}%
      </span>
    </div>
  );
}


function Selector({
  label,
  value,
  onChange,
  options,
  optional = false,
  disabled = false,
}) {
  return (
    <label className="rx-onb-admin-field">

      <span>
        {label}

        {
          optional
          && (
            <small>
              Optional
            </small>
          )
        }
      </span>

      <select
        value={
          value
          || ""
        }
        disabled={
          disabled
        }
        onChange={
          (event) =>
            onChange(
              event.target.value
            )
        }
      >
        <option value="">
          {
            optional
              ? "Not specified"
              : "Select..."
          }
        </option>

        {
          options.map(
            (option) => (
              <option
                key={
                  option.id
                  || option.value
                }
                value={
                  option.id
                  || option.value
                }
              >
                {
                  option.name
                  || option.label
                  || option.value
                }

                {
                  option.email
                    ? ` — ${option.email}`
                    : ""
                }
              </option>
            )
          )
        }
      </select>

    </label>
  );
}


const COMPLETION_RULES = {
  information:
    "acknowledged",

  structured_form:
    "form_complete",

  agreement:
    "agreement_complete",

  document_upload:
    "upload_complete",

  secure_payroll:
    "secure_payroll_complete",

  declaration:
    "declaration_complete",
};


function newComponent(
  sequence
) {
  return {
    key:
      `step_${sequence}`,

    label:
      `Step ${sequence}`,

    component_type:
      "information",

    sequence,

    required:
      true,

    blocking:
      true,

    completion_rule:
      "acknowledged",

    description:
      "",

    instructions:
      "",

    information_body:
      "",

    form_fields:
      [],

    agreement_template_key:
      "",

    document_requirements:
      [],

    secure_workflow_key:
      "",

    declaration_text:
      "",

    metadata:
      {},
  };
}


function newPack() {
  return {
    id: null,

    pack_key:
      "",

    version:
      1,

    name:
      "",

    description:
      "",

    status:
      "draft",

    audience_types:
      [],

    contractor_ids:
      [],

    client_ids:
      [],

    site_ids:
      [],

    role_names:
      [],

    components: [
      newComponent(1),
    ],
  };
}


function normalisePack(
  pack
) {
  return {
    ...newPack(),
    ...pack,

    id:
      pack?.id
      || pack?._id
      || null,

    components:
      (
        pack?.components
        || []
      )
      .slice()
      .sort(
        (
          left,
          right
        ) =>
          Number(
            left.sequence
            || 0
          )
          -
          Number(
            right.sequence
            || 0
          )
      ),
  };
}


function PackComponentEditor({
  component,
  index,
  count,
  agreementTemplates,
  onChange,
  onMove,
  onDelete,
}) {

  const update = (
    field,
    value
  ) => {
    onChange(
      {
        ...component,
        [field]: value,
      }
    );
  };


  const changeType = (
    type
  ) => {
    onChange(
      {
        ...component,

        component_type:
          type,

        completion_rule:
          COMPLETION_RULES[
            type
          ],
      }
    );
  };


  const addField = () => {

    const next =
      (
        component.form_fields
        || []
      ).length + 1;

    update(
      "form_fields",
      [
        ...(
          component.form_fields
          || []
        ),

        {
          key:
            `field_${next}`,

          label:
            `Field ${next}`,

          field_type:
            "text",

          required:
            true,

          placeholder:
            null,

          help_text:
            null,

          options:
            [],

          validation:
            {},

          sensitivity:
            "standard",
        },
      ]
    );
  };


  const updateField = (
    fieldIndex,
    patch
  ) => {

    const fields = [
      ...(
        component.form_fields
        || []
      ),
    ];

    fields[
      fieldIndex
    ] = {
      ...fields[
        fieldIndex
      ],
      ...patch,
    };

    update(
      "form_fields",
      fields
    );
  };


  const removeField = (
    fieldIndex
  ) => {

    update(
      "form_fields",
      (
        component.form_fields
        || []
      ).filter(
        (
          _,
          current
        ) =>
          current
          !== fieldIndex
      )
    );
  };


  const addDocument = () => {

    const next =
      (
        component
          .document_requirements
        || []
      ).length + 1;

    update(
      "document_requirements",
      [
        ...(
          component
            .document_requirements
          || []
        ),

        {
          key:
            `document_${next}`,

          label:
            `Document ${next}`,

          required:
            true,

          accepted_types: [
            "application/pdf",
            "image/jpeg",
            "image/png",
          ],

          description:
            null,

          expiry_required:
            false,
        },
      ]
    );
  };


  const updateDocument = (
    documentIndex,
    patch
  ) => {

    const documents = [
      ...(
        component
          .document_requirements
        || []
      ),
    ];

    documents[
      documentIndex
    ] = {
      ...documents[
        documentIndex
      ],
      ...patch,
    };

    update(
      "document_requirements",
      documents
    );
  };


  const removeDocument = (
    documentIndex
  ) => {

    update(
      "document_requirements",
      (
        component
          .document_requirements
        || []
      ).filter(
        (
          _,
          current
        ) =>
          current
          !== documentIndex
      )
    );
  };


  return (
    <div className="rx-pack-component">

      <div className="rx-pack-component__header">

        <div className="rx-pack-component__number">
          {index + 1}
        </div>

        <div>
          <strong>
            {
              component.label
              || "Untitled step"
            }
          </strong>

          <small>
            {
              component
                .component_type
                ?.replaceAll(
                  "_",
                  " "
                )
            }
          </small>
        </div>

        <div className="rx-pack-component__actions">

          <button
            type="button"
            disabled={
              index === 0
            }
            onClick={
              () =>
                onMove(
                  index,
                  -1
                )
            }
          >
            ↑
          </button>

          <button
            type="button"
            disabled={
              index
              === count - 1
            }
            onClick={
              () =>
                onMove(
                  index,
                  1
                )
            }
          >
            ↓
          </button>

          <button
            type="button"
            className="rx-pack-delete"
            onClick={
              () =>
                onDelete(
                  index
                )
            }
          >
            Remove
          </button>

        </div>

      </div>


      <div className="rx-pack-component__grid">

        <label>
          Component key

          <input
            value={
              component.key
              || ""
            }
            onChange={
              (event) =>
                update(
                  "key",
                  event
                    .target
                    .value
                )
            }
          />
        </label>


        <label>
          Label

          <input
            value={
              component.label
              || ""
            }
            onChange={
              (event) =>
                update(
                  "label",
                  event
                    .target
                    .value
                )
            }
          />
        </label>


        <label>
          Type

          <select
            value={
              component
                .component_type
            }
            onChange={
              (event) =>
                changeType(
                  event
                    .target
                    .value
                )
            }
          >
            <option value="information">
              Information / Welcome
            </option>

            <option value="structured_form">
              Structured Form
            </option>

            <option value="agreement">
              Agreement
            </option>

            <option value="document_upload">
              Document Upload
            </option>

            <option value="secure_payroll">
              Secure Payroll
            </option>

            <option value="declaration">
              Declaration
            </option>
          </select>
        </label>


        <label>
          Completion rule

          <input
            value={
              component
                .completion_rule
              || ""
            }
            readOnly
          />
        </label>

      </div>


      <div className="rx-pack-checks">

        <label>
          <input
            type="checkbox"
            checked={
              component.required
              !== false
            }
            onChange={
              (event) =>
                update(
                  "required",
                  event.target.checked
                )
            }
          />
          Required
        </label>

        <label>
          <input
            type="checkbox"
            checked={
              component.blocking
              !== false
            }
            onChange={
              (event) =>
                update(
                  "blocking",
                  event.target.checked
                )
            }
          />
          Blocks overall completion
        </label>

      </div>


      <label className="rx-pack-full-field">
        Description

        <textarea
          rows={2}
          value={
            component.description
            || ""
          }
          onChange={
            (event) =>
              update(
                "description",
                event.target.value
              )
          }
        />
      </label>


      <label className="rx-pack-full-field">
        Worker instructions

        <textarea
          rows={2}
          value={
            component.instructions
            || ""
          }
          onChange={
            (event) =>
              update(
                "instructions",
                event.target.value
              )
          }
        />
      </label>


      {
        component.component_type
        === "information"
        && (
          <label className="rx-pack-full-field">
            Information / welcome content

            <textarea
              rows={6}
              value={
                component
                  .information_body
                || ""
              }
              onChange={
                (event) =>
                  update(
                    "information_body",
                    event
                      .target
                      .value
                  )
              }
            />
          </label>
        )
      }


      {
        component.component_type
        === "agreement"
        && (
          <div className="rx-pack-special">

            <label>
              Agreement template

              <select
                value={
                  component
                    .agreement_template_key
                  || ""
                }
                onChange={
                  (event) =>
                    update(
                      "agreement_template_key",
                      event
                        .target
                        .value
                    )
                }
              >
                <option value="">
                  Select Agreement template...
                </option>

                {
                  agreementTemplates.map(
                    (
                      template
                    ) => (
                      <option
                        key={
                          template
                            .template_key
                        }
                        value={
                          template
                            .template_key
                        }
                      >
                        {
                          template.name
                          || template.title
                          || template
                            .template_key
                        }
                        {
                          template.version
                            ? ` — v${template.version}`
                            : ""
                        }
                        {
                          template.status
                            ? ` — ${template.status}`
                            : ""
                        }
                      </option>
                    )
                  )
                }
              </select>
            </label>

            {
              agreementTemplates.length
              === 0
              && (
                <div className="rx-pack-hint">
                  No Agreement templates are currently
                  available. Create the legal document
                  first under Agreements &amp; eSign.
                </div>
              )
            }

          </div>
        )
      }


      {
        component.component_type
        === "secure_payroll"
        && (
          <div className="rx-pack-special">

            <label>
              Secure workflow key

              <input
                value={
                  component
                    .secure_workflow_key
                  || ""
                }
                placeholder="e.g. employee_payroll_onboarding"
                onChange={
                  (event) =>
                    update(
                      "secure_workflow_key",
                      event
                        .target
                        .value
                    )
                }
              />
            </label>

            <div className="rx-pack-warning">
              Workflow reference only. TFN, BSB,
              account number and other payroll secrets
              must not be entered here.
            </div>

          </div>
        )
      }


      {
        component.component_type
        === "declaration"
        && (
          <label className="rx-pack-full-field">
            Declaration wording

            <textarea
              rows={5}
              value={
                component
                  .declaration_text
                || ""
              }
              onChange={
                (event) =>
                  update(
                    "declaration_text",
                    event
                      .target
                      .value
                  )
              }
            />
          </label>
        )
      }


      {
        component.component_type
        === "structured_form"
        && (
          <div className="rx-pack-builder">

            <div className="rx-pack-builder__heading">

              <strong>
                Form Fields
              </strong>

              <button
                type="button"
                onClick={
                  addField
                }
              >
                + Add field
              </button>

            </div>

            {
              (
                component
                  .form_fields
                || []
              ).length === 0
              ? (
                <div className="rx-pack-empty-small">
                  No fields configured.
                </div>
              )
              : (
                component
                  .form_fields
                  .map(
                    (
                      field,
                      fieldIndex
                    ) => (
                      <div
                        className="rx-pack-builder-row"
                        key={
                          `${field.key}-${fieldIndex}`
                        }
                      >

                        <input
                          placeholder="Field key"
                          value={
                            field.key
                            || ""
                          }
                          onChange={
                            (event) =>
                              updateField(
                                fieldIndex,
                                {
                                  key:
                                    event
                                      .target
                                      .value,
                                }
                              )
                          }
                        />

                        <input
                          placeholder="Label"
                          value={
                            field.label
                            || ""
                          }
                          onChange={
                            (event) =>
                              updateField(
                                fieldIndex,
                                {
                                  label:
                                    event
                                      .target
                                      .value,
                                }
                              )
                          }
                        />

                        <select
                          value={
                            field
                              .field_type
                            || "text"
                          }
                          onChange={
                            (event) =>
                              updateField(
                                fieldIndex,
                                {
                                  field_type:
                                    event
                                      .target
                                      .value,
                                }
                              )
                          }
                        >
                          <option value="text">
                            Text
                          </option>

                          <option value="textarea">
                            Long text
                          </option>

                          <option value="email">
                            Email
                          </option>

                          <option value="phone">
                            Phone
                          </option>

                          <option value="date">
                            Date
                          </option>

                          <option value="number">
                            Number
                          </option>
                        </select>

                        <label className="rx-pack-inline-check">
                          <input
                            type="checkbox"
                            checked={
                              field.required
                              === true
                            }
                            onChange={
                              (event) =>
                                updateField(
                                  fieldIndex,
                                  {
                                    required:
                                      event
                                        .target
                                        .checked,
                                  }
                                )
                            }
                          />
                          Required
                        </label>

                        <button
                          type="button"
                          className="rx-pack-delete"
                          onClick={
                            () =>
                              removeField(
                                fieldIndex
                              )
                          }
                        >
                          ×
                        </button>

                      </div>
                    )
                  )
              )
            }

          </div>
        )
      }


      {
        component.component_type
        === "document_upload"
        && (
          <div className="rx-pack-builder">

            <div className="rx-pack-builder__heading">

              <strong>
                Required Documents
              </strong>

              <button
                type="button"
                onClick={
                  addDocument
                }
              >
                + Add document
              </button>

            </div>

            {
              (
                component
                  .document_requirements
                || []
              ).length === 0
              ? (
                <div className="rx-pack-empty-small">
                  No document requirements configured.
                </div>
              )
              : (
                component
                  .document_requirements
                  .map(
                    (
                      document,
                      documentIndex
                    ) => (
                      <div
                        className="rx-pack-builder-row rx-pack-builder-row--document"
                        key={
                          `${document.key}-${documentIndex}`
                        }
                      >

                        <input
                          placeholder="Document key"
                          value={
                            document.key
                            || ""
                          }
                          onChange={
                            (event) =>
                              updateDocument(
                                documentIndex,
                                {
                                  key:
                                    event
                                      .target
                                      .value,
                                }
                              )
                          }
                        />

                        <input
                          placeholder="Document label"
                          value={
                            document.label
                            || ""
                          }
                          onChange={
                            (event) =>
                              updateDocument(
                                documentIndex,
                                {
                                  label:
                                    event
                                      .target
                                      .value,
                                }
                              )
                          }
                        />

                        <label className="rx-pack-inline-check">
                          <input
                            type="checkbox"
                            checked={
                              document.required
                              !== false
                            }
                            onChange={
                              (event) =>
                                updateDocument(
                                  documentIndex,
                                  {
                                    required:
                                      event
                                        .target
                                        .checked,
                                  }
                                )
                            }
                          />
                          Required
                        </label>

                        <label className="rx-pack-inline-check">
                          <input
                            type="checkbox"
                            checked={
                              document
                                .expiry_required
                              === true
                            }
                            onChange={
                              (event) =>
                                updateDocument(
                                  documentIndex,
                                  {
                                    expiry_required:
                                      event
                                        .target
                                        .checked,
                                  }
                                )
                            }
                          />
                          Expiry
                        </label>

                        <button
                          type="button"
                          className="rx-pack-delete"
                          onClick={
                            () =>
                              removeDocument(
                                documentIndex
                              )
                          }
                        >
                          ×
                        </button>

                      </div>
                    )
                  )
              )
            }

          </div>
        )
      }

    </div>
  );
}


function PackStudio({
  referenceData,
  onPackActivated,
}) {

  const [packs, setPacks] =
    useState([]);

  const [schema, setSchema] =
    useState(null);

  const [
    agreementTemplates,
    setAgreementTemplates
  ] = useState([]);

  const [editor, setEditor] =
    useState(
      newPack()
    );


  // V1.0E-R4-R3-R2 NEW PACK UX CONTROL
  const packKeyRef =
    useRef(null);

  const [versions, setVersions] =
    useState([]);

  const [busy, setBusy] =
    useState(false);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [notice, setNotice] =
    useState("");


  const loadPacks =
    useCallback(
      async () => {

        const payload =
          await onboardingApi(
            "/admin/packs"
          );

        setPacks(
          payload.packs
          || []
        );
      },
      []
    );


  const loadAgreementTemplates =
    useCallback(
      async () => {

        try {

          const payload =
            await fetchJson(
              `${AGREEMENT_API}/templates`
            );

          const templates =
            payload?.templates
            || payload?.items
            || (
              Array.isArray(
                payload
              )
                ? payload
                : []
            );

          setAgreementTemplates(
            templates
          );

        } catch {

          setAgreementTemplates(
            []
          );
        }
      },
      []
    );


  const loadAll =
    useCallback(
      async () => {

        setLoading(true);
        setError("");

        try {

          const [
            schemaPayload
          ] = await Promise.all([
            onboardingApi(
              "/admin/schema"
            ),

            loadPacks(),

            loadAgreementTemplates(),
          ]);

          setSchema(
            schemaPayload
          );

        } catch (err) {

          setError(
            err.message
          );

        } finally {

          setLoading(false);
        }
      },
      [
        loadPacks,
        loadAgreementTemplates,
      ]
    );


  useEffect(
    () => {
      loadAll();
    },
    [loadAll]
  );


  const startNewPack =
    () => {

      setEditor(
        newPack()
      );

      setVersions(
        []
      );

      setError(
        ""
      );

      setNotice(
        "New onboarding pack workspace ready."
      );

      window.requestAnimationFrame(
        () => {
          packKeyRef
            .current
            ?.focus();
        }
      );
    };


  const setPackField = (
    field,
    value
  ) => {

    setEditor(
      (current) => ({
        ...current,
        [field]: value,
      })
    );
  };


  const normaliseSequences = (
    components
  ) => {

    return components.map(
      (
        component,
        index
      ) => ({
        ...component,
        sequence:
          index + 1,
      })
    );
  };


  const updateComponent = (
    index,
    component
  ) => {

    setEditor(
      (current) => {

        const components = [
          ...current.components,
        ];

        components[
          index
        ] = component;

        return {
          ...current,
          components:
            normaliseSequences(
              components
            ),
        };
      }
    );
  };


  const moveComponent = (
    index,
    direction
  ) => {

    setEditor(
      (current) => {

        const components = [
          ...current.components,
        ];

        const target =
          index
          + direction;

        if (
          target < 0
          || target
          >= components.length
        ) {
          return current;
        }

        const temporary =
          components[index];

        components[index] =
          components[target];

        components[target] =
          temporary;

        return {
          ...current,
          components:
            normaliseSequences(
              components
            ),
        };
      }
    );
  };


  const removeComponent = (
    index
  ) => {

    setEditor(
      (current) => ({
        ...current,

        components:
          normaliseSequences(
            current
              .components
              .filter(
                (
                  _,
                  currentIndex
                ) =>
                  currentIndex
                  !== index
              )
          ),
      })
    );
  };


  const addComponent =
    () => {

      setEditor(
        (current) => {

          const next =
            current
              .components
              .length
            + 1;

          return {
            ...current,

            components: [
              ...current.components,
              newComponent(
                next
              ),
            ],
          };
        }
      );
    };


  const payloadForSave =
    () => ({
      tenant_id:
        "server-controlled",

      pack_key:
        editor.pack_key,

      version:
        Number(
          editor.version
          || 1
        ),

      name:
        editor.name,

      description:
        editor.description
        || null,

      status:
        editor.status
        || "draft",

      audience_types:
        editor.audience_types
        || [],

      contractor_ids:
        editor.contractor_ids
        || [],

      client_ids:
        editor.client_ids
        || [],

      site_ids:
        editor.site_ids
        || [],

      role_names:
        editor.role_names
        || [],

      components:
        normaliseSequences(
          editor.components
          || []
        ),
    });


  const saveDraft =
    async () => {

      setBusy(true);
      setError("");
      setNotice("");

      try {

        let payload;

        if (editor.id) {

          payload =
            await onboardingApi(
              `/admin/packs/${editor.id}`,
              {
                method:
                  "PUT",

                body:
                  JSON.stringify(
                    payloadForSave()
                  ),
              }
            );

        } else {

          payload =
            await onboardingApi(
              "/admin/packs",
              {
                method:
                  "POST",

                body:
                  JSON.stringify(
                    payloadForSave()
                  ),
              }
            );
        }

        const saved =
          payload.pack
          || payload;

        setEditor(
          normalisePack(
            saved
          )
        );

        setNotice(
          editor.id
            ? "Draft onboarding pack updated."
            : "Draft onboarding pack created."
        );

        await loadPacks();

      } catch (err) {

        setError(
          err.message
        );

      } finally {

        setBusy(false);
      }
    };


  const editPack =
    async (
      pack
    ) => {

      setError("");
      setNotice("");
      setVersions([]);

      try {

        const id =
          pack.id
          || pack._id;

        const payload =
          await onboardingApi(
            `/admin/packs/${id}`
          );

        setEditor(
          normalisePack(
            payload.pack
            || payload
          )
        );

      } catch (err) {

        setError(
          err.message
        );
      }
    };


  const activatePack =
    async () => {

      if (
        !editor.id
      ) {
        setError(
          "Save the draft before activation."
        );
        return;
      }

      if (
        editor.status
        !== "draft"
      ) {
        return;
      }

      if (
        !window.confirm(
          `Activate ${editor.name} v${editor.version}? `
          + "The activated version will become immutable."
        )
      ) {
        return;
      }

      setBusy(true);
      setError("");
      setNotice("");

      try {

        const payload =
          await onboardingApi(
            `/admin/packs/${editor.id}/activate`,
            {
              method:
                "POST",
            }
          );

        setEditor(
          normalisePack(
            payload.pack
            || payload
          )
        );

        setNotice(
          "Pack activated and frozen successfully."
        );

        await loadPacks();

        if (
          onPackActivated
        ) {
          await onPackActivated();
        }

      } catch (err) {

        setError(
          err.message
        );

      } finally {

        setBusy(false);
      }
    };


  const createNewVersion =
    async () => {

      if (
        !editor.id
        || editor.status
        !== "active"
      ) {
        return;
      }

      setBusy(true);
      setError("");
      setNotice("");

      try {

        const payload =
          await onboardingApi(
            `/admin/packs/${editor.id}/new-version`,
            {
              method:
                "POST",
            }
          );

        setEditor(
          normalisePack(
            payload.pack
            || payload
          )
        );

        setNotice(
          `New DRAFT version ${payload.new_version} created.`
        );

        await loadPacks();

      } catch (err) {

        setError(
          err.message
        );

      } finally {

        setBusy(false);
      }
    };


  const deleteDraft =
    async () => {

      if (
        !editor.id
        || editor.status
        !== "draft"
      ) {
        return;
      }

      if (
        !window.confirm(
          `Delete draft ${editor.name || editor.pack_key}?`
        )
      ) {
        return;
      }

      setBusy(true);
      setError("");

      try {

        await onboardingApi(
          `/admin/packs/${editor.id}`,
          {
            method:
              "DELETE",
          }
        );

        setEditor(
          newPack()
        );

        setVersions([]);

        setNotice(
          "Draft pack deleted."
        );

        await loadPacks();

      } catch (err) {

        setError(
          err.message
        );

      } finally {

        setBusy(false);
      }
    };


  const viewHistory =
    async () => {

      if (
        !editor.pack_key
      ) {
        return;
      }

      setError("");

      try {

        const payload =
          await onboardingApi(
            `/admin/pack-keys/${encodeURIComponent(
              editor.pack_key
            )}/versions`
          );

        setVersions(
          payload.versions
          || []
        );

      } catch (err) {

        setError(
          err.message
        );
      }
    };


  const immutable =
    editor.status
    !== "draft";


  const counts =
    useMemo(
      () => {

        return {
          draft:
            packs.filter(
              (pack) =>
                pack.status
                === "draft"
            ).length,

          active:
            packs.filter(
              (pack) =>
                pack.status
                === "active"
            ).length,

          retired:
            packs.filter(
              (pack) =>
                pack.status
                === "retired"
            ).length,
        };
      },
      [packs]
    );


  if (loading) {
    return (
      <div className="rx-onb-admin-empty">
        Loading Pack Studio...
      </div>
    );
  }


  return (
    <div className="rx-pack-studio">

      {
        error
        && (
          <div className="rx-onb-admin-alert rx-onb-admin-alert--error">
            {error}
          </div>
        )
      }

      {
        notice
        && (
          <div className="rx-onb-admin-alert rx-onb-admin-alert--success">
            {notice}
          </div>
        )
      }


      <div className="rx-pack-summary">

        <div>
          <strong>
            {counts.draft}
          </strong>
          <span>
            Draft
          </span>
        </div>

        <div>
          <strong>
            {counts.active}
          </strong>
          <span>
            Active
          </span>
        </div>

        <div>
          <strong>
            {counts.retired}
          </strong>
          <span>
            Retired
          </span>
        </div>

        <div>
          <strong>
            {packs.length}
          </strong>
          <span>
            Total
          </span>
        </div>

      </div>


      <div className="rx-pack-layout">

        <aside className="rx-pack-register">

          <div className="rx-pack-register__heading">

            <div>
              <strong>
                Pack Register
              </strong>

              <small>
                Version-controlled onboarding packs
              </small>
            </div>

            <button
              type="button"
              onClick={
                startNewPack
              }
              title="Start a new onboarding pack draft"
            >
              + New
            </button>

          </div>


          {
            packs.length === 0
            ? (
              <div className="rx-pack-empty-small">
                No onboarding packs yet.
              </div>
            )
            : packs.map(
                (pack) => (

                  <button
                    type="button"
                    className={
                      (
                        editor.id
                        === (
                          pack.id
                          || pack._id
                        )
                      )
                        ? "rx-pack-register-item rx-pack-register-item--selected"
                        : "rx-pack-register-item"
                    }
                    key={
                      pack.id
                      || pack._id
                    }
                    onClick={
                      () =>
                        editPack(
                          pack
                        )
                    }
                  >

                    <div>
                      <strong>
                        {pack.name}
                      </strong>

                      <small>
                        {
                          pack.pack_key
                        }
                        {" · v"}
                        {
                          pack.version
                        }
                      </small>
                    </div>

                    <Badge
                      status={
                        pack.status
                      }
                    />

                  </button>
                )
              )
          }

        </aside>


        <section className="rx-pack-editor">

          <div className="rx-pack-editor__heading">

            <div>
              <h2>
                {
                  editor.id
                    ? editor.name
                      || "Onboarding Pack"
                    : "New Onboarding Pack"
                }
              </h2>

              <p>
                Build the ordered onboarding experience.
                ACTIVE and RETIRED versions are immutable.
              </p>


              {
                !editor.id
                && (
                  <div className="rx-pack-new-state">
                    NEW DRAFT WORKSPACE
                  </div>
                )
              }
            </div>

            <Badge
              status={
                editor.status
              }
            />

          </div>


          <fieldset
            disabled={
              immutable
            }
            className="rx-pack-fieldset"
          >

            <div className="rx-pack-main-grid">

              <label>
                Pack key

                <input
                  ref={
                    packKeyRef
                  }
                  value={
                    editor.pack_key
                  }
                  placeholder="e.g. bsms_employee_onboarding"
                  disabled={
                    Boolean(
                      editor.id
                    )
                  }
                  onChange={
                    (event) =>
                      setPackField(
                        "pack_key",
                        event
                          .target
                          .value
                      )
                  }
                />
              </label>


              <label>
                Pack name

                <input
                  value={
                    editor.name
                  }
                  placeholder="e.g. BSMS Employee Onboarding"
                  onChange={
                    (event) =>
                      setPackField(
                        "name",
                        event
                          .target
                          .value
                      )
                  }
                />
              </label>


              <label>
                Version

                <input
                  value={
                    editor.version
                    || 1
                  }
                  readOnly
                />
              </label>

            </div>


            <label className="rx-pack-full-field">
              Description

              <textarea
                rows={3}
                value={
                  editor.description
                  || ""
                }
                onChange={
                  (event) =>
                    setPackField(
                      "description",
                      event
                        .target
                        .value
                    )
                }
              />
            </label>


            <div className="rx-pack-targeting">

              <h3>
                Assignment Scope
              </h3>

              <p>
                Leave a dimension empty to make it unrestricted.
                Values within a dimension use OR; configured
                dimensions are combined using AND.
              </p>


              <div className="rx-pack-main-grid">

                <label>
                  Audience type

                  <select
                    value={
                      editor
                        .audience_types
                        ?.[0]
                      || ""
                    }
                    onChange={
                      (event) =>
                        setPackField(
                          "audience_types",
                          event
                            .target
                            .value
                            ? [
                                event
                                  .target
                                  .value
                              ]
                            : []
                        )
                    }
                  >
                    <option value="">
                      Unrestricted
                    </option>

                    {
                      (
                        schema
                          ?.audience_types
                        || []
                      ).map(
                        (value) => (
                          <option
                            key={
                              value
                            }
                            value={
                              value
                            }
                          >
                            {
                              value
                                .replaceAll(
                                  "_",
                                  " "
                                )
                            }
                          </option>
                        )
                      )
                    }
                  </select>
                </label>


                <label>
                  Contractor

                  <select
                    value={
                      editor
                        .contractor_ids
                        ?.[0]
                      || ""
                    }
                    onChange={
                      (event) =>
                        setPackField(
                          "contractor_ids",
                          event
                            .target
                            .value
                            ? [
                                event
                                  .target
                                  .value
                              ]
                            : []
                        )
                    }
                  >
                    <option value="">
                      Unrestricted
                    </option>

                    {
                      (
                        referenceData
                          .contractors
                        || []
                      ).map(
                        (item) => (
                          <option
                            key={
                              item.id
                            }
                            value={
                              item.id
                            }
                          >
                            {
                              item.name
                            }
                          </option>
                        )
                      )
                    }
                  </select>
                </label>


                <label>
                  Client

                  <select
                    value={
                      editor
                        .client_ids
                        ?.[0]
                      || ""
                    }
                    onChange={
                      (event) =>
                        setPackField(
                          "client_ids",
                          event
                            .target
                            .value
                            ? [
                                event
                                  .target
                                  .value
                              ]
                            : []
                        )
                    }
                  >
                    <option value="">
                      Unrestricted
                    </option>

                    {
                      (
                        referenceData
                          .clients
                        || []
                      ).map(
                        (item) => (
                          <option
                            key={
                              item.id
                            }
                            value={
                              item.id
                            }
                          >
                            {
                              item.name
                            }
                          </option>
                        )
                      )
                    }
                  </select>
                </label>


                <label>
                  Site

                  <select
                    value={
                      editor
                        .site_ids
                        ?.[0]
                      || ""
                    }
                    onChange={
                      (event) =>
                        setPackField(
                          "site_ids",
                          event
                            .target
                            .value
                            ? [
                                event
                                  .target
                                  .value
                              ]
                            : []
                        )
                    }
                  >
                    <option value="">
                      Unrestricted
                    </option>

                    {
                      (
                        referenceData
                          .sites
                        || []
                      ).map(
                        (item) => (
                          <option
                            key={
                              item.id
                            }
                            value={
                              item.id
                            }
                          >
                            {
                              item.name
                            }
                          </option>
                        )
                      )
                    }
                  </select>
                </label>

              </div>

            </div>


            <div className="rx-pack-components-heading">

              <div>
                <h3>
                  Pack Components
                </h3>

                <p>
                  Components execute in the order shown below.
                </p>
              </div>

              <button
                type="button"
                onClick={
                  addComponent
                }
              >
                + Add Component
              </button>

            </div>


            <div className="rx-pack-component-list">

              {
                editor.components.length
                === 0
                ? (
                  <div className="rx-pack-empty-small">
                    Add at least one component
                    before activation.
                  </div>
                )
                : editor.components.map(
                    (
                      component,
                      index
                    ) => (
                      <PackComponentEditor
                        key={
                          `${
                            component.key
                          }-${index}`
                        }
                        component={
                          component
                        }
                        index={
                          index
                        }
                        count={
                          editor
                            .components
                            .length
                        }
                        agreementTemplates={
                          agreementTemplates
                        }
                        onChange={
                          (
                            updated
                          ) =>
                            updateComponent(
                              index,
                              updated
                            )
                        }
                        onMove={
                          moveComponent
                        }
                        onDelete={
                          removeComponent
                        }
                      />
                    )
                  )
              }

            </div>

          </fieldset>


          <div className="rx-pack-editor-actions">

            {
              !immutable
              && (
                <>
                  <button
                    type="button"
                    className="rx-onb-admin-button rx-onb-admin-button--primary"
                    disabled={
                      busy
                      || !editor.name
                      || !editor.pack_key
                    }
                    onClick={
                      saveDraft
                    }
                  >
                    {
                      busy
                        ? "Saving..."
                        : editor.id
                        ? "Save Draft"
                        : "Create Draft"
                    }
                  </button>

                  {
                    editor.id
                    && (
                      <button
                        type="button"
                        className="rx-onb-admin-button rx-onb-admin-button--activate"
                        disabled={
                          busy
                          || editor.components
                            .length === 0
                        }
                        onClick={
                          activatePack
                        }
                      >
                        Activate & Freeze
                      </button>
                    )
                  }

                  {
                    editor.id
                    && (
                      <button
                        type="button"
                        className="rx-onb-admin-button rx-onb-admin-button--danger"
                        disabled={
                          busy
                        }
                        onClick={
                          deleteDraft
                        }
                      >
                        Delete Draft
                      </button>
                    )
                  }
                </>
              )
            }


            {
              editor.status
              === "active"
              && (
                <button
                  type="button"
                  className="rx-onb-admin-button rx-onb-admin-button--primary"
                  disabled={
                    busy
                  }
                  onClick={
                    createNewVersion
                  }
                >
                  Create New Draft Version
                </button>
              )
            }


            {
              editor.pack_key
              && (
                <button
                  type="button"
                  className="rx-onb-admin-button rx-onb-admin-button--secondary"
                  onClick={
                    viewHistory
                  }
                >
                  Version History
                </button>
              )
            }

          </div>


          {
            versions.length > 0
            && (
              <div className="rx-pack-history">

                <h3>
                  Version History
                </h3>

                {
                  versions.map(
                    (version) => (
                      <button
                        type="button"
                        key={
                          version.id
                          || version._id
                        }
                        onClick={
                          () =>
                            editPack(
                              version
                            )
                        }
                      >
                        <span>
                          v{
                            version.version
                          }
                        </span>

                        <strong>
                          {
                            version.name
                          }
                        </strong>

                        <Badge
                          status={
                            version.status
                          }
                        />
                      </button>
                    )
                  )
                }

              </div>
            )
          }

        </section>

      </div>

    </div>
  );
}


function AssignmentRegister({
  packs,
  refs,
  sessions,
  loading,
  reload,
}) {

  const [
    selectedPack,
    setSelectedPack
  ] = useState("");

  const [
    selectedWorker,
    setSelectedWorker
  ] = useState("");

  const [
    contractorId,
    setContractorId
  ] = useState("");

  const [
    clientId,
    setClientId
  ] = useState("");

  const [
    siteId,
    setSiteId
  ] = useState("");

  const [
    audienceType,
    setAudienceType
  ] = useState("");

  const [preview, setPreview] =
    useState(null);

  const [
    selectedSession,
    setSelectedSession
  ] = useState(null);

  const [busy, setBusy] =
    useState(false);

  const [error, setError] =
    useState("");

  const [notice, setNotice] =
    useState("");


  const packOptions =
    packs.map(
      (pack) => ({
        id:
          pack.id
          || pack._id,

        name:
          `${pack.name} — v${pack.version}`,
      })
    );


  const audienceOptions =
    (
      refs.audience_types
      || []
    ).map(
      (value) => ({
        value,

        label:
          value
            .replaceAll(
              "_",
              " "
            ),
      })
    );


  const requestBody = {
    pack_id:
      selectedPack,

    user_id:
      selectedWorker,

    contractor_id:
      contractorId
      || null,

    client_id:
      clientId
      || null,

    site_id:
      siteId
      || null,

    audience_type:
      audienceType
      || null,
  };


  const clearPreview =
    () => {
      setPreview(null);
      setNotice("");
    };


  const previewAssignment =
    async () => {

      if (
        !selectedPack
        || !selectedWorker
      ) {
        setError(
          "Select an ACTIVE onboarding pack and worker first."
        );

        return;
      }

      setBusy(true);
      setError("");
      setNotice("");
      setPreview(null);

      try {

        const payload =
          await onboardingApi(
            "/admin/assignment-preview",
            {
              method:
                "POST",

              body:
                JSON.stringify(
                  requestBody
                ),
            }
          );

        setPreview(
          payload
        );

      } catch (err) {

        setError(
          err.message
        );

      } finally {

        setBusy(false);
      }
    };


  const createSession =
    async () => {

      if (
        !preview?.would_create
      ) {
        return;
      }

      setBusy(true);
      setError("");
      setNotice("");

      try {

        await onboardingApi(
          "/admin/sessions",
          {
            method:
              "POST",

            body:
              JSON.stringify(
                requestBody
              ),
          }
        );

        setNotice(
          "Onboarding session created. No invitation email was sent."
        );

        setPreview(null);

        await reload();

      } catch (err) {

        setError(
          err.message
        );

      } finally {

        setBusy(false);
      }
    };


  const openSession =
    async (
      session
    ) => {

      try {

        const payload =
          await onboardingApi(
            `/admin/sessions/${
              session.id
              || session._id
            }`
          );

        setSelectedSession(
          payload
        );

      } catch (err) {

        setError(
          err.message
        );
      }
    };


  return (
    <>

      {
        error
        && (
          <div className="rx-onb-admin-alert rx-onb-admin-alert--error">
            {error}
          </div>
        )
      }

      {
        notice
        && (
          <div className="rx-onb-admin-alert rx-onb-admin-alert--success">
            {notice}
          </div>
        )
      }


      <section className="rx-onb-admin-card">

        <div className="rx-onb-admin-card-heading">

          <div>
            <h2>
              Assign Onboarding
            </h2>

            <p>
              Nothing is created until assignment
              preview passes and Create Session is
              explicitly selected.
            </p>
          </div>

          <Badge
            status="controlled"
          />

        </div>


        <div className="rx-onb-admin-form-grid">

          <Selector
            label="Active onboarding pack"
            value={
              selectedPack
            }
            onChange={
              (value) => {
                setSelectedPack(
                  value
                );
                clearPreview();
              }
            }
            options={
              packOptions
            }
          />


          <Selector
            label="Staff / contractor worker"
            value={
              selectedWorker
            }
            onChange={
              (value) => {
                setSelectedWorker(
                  value
                );
                clearPreview();
              }
            }
            options={
              refs.workers
              || []
            }
          />


          <Selector
            label="Contractor"
            optional
            value={
              contractorId
            }
            onChange={
              (value) => {
                setContractorId(
                  value
                );
                clearPreview();
              }
            }
            options={
              refs.contractors
              || []
            }
          />


          <Selector
            label="Client"
            optional
            value={
              clientId
            }
            onChange={
              (value) => {
                setClientId(
                  value
                );
                clearPreview();
              }
            }
            options={
              refs.clients
              || []
            }
          />


          <Selector
            label="Site"
            optional
            value={
              siteId
            }
            onChange={
              (value) => {
                setSiteId(
                  value
                );
                clearPreview();
              }
            }
            options={
              refs.sites
              || []
            }
          />


          <Selector
            label="Engagement / audience"
            optional
            value={
              audienceType
            }
            onChange={
              (value) => {
                setAudienceType(
                  value
                );
                clearPreview();
              }
            }
            options={
              audienceOptions
            }
          />

        </div>


        <div className="rx-onb-admin-actions">

          <button
            type="button"
            className="rx-onb-admin-button rx-onb-admin-button--secondary"
            disabled={
              busy
              || !selectedPack
              || !selectedWorker
            }
            onClick={
              previewAssignment
            }
          >
            Preview Assignment
          </button>

          <button
            type="button"
            className="rx-onb-admin-button rx-onb-admin-button--primary"
            disabled={
              busy
              || !preview
                ?.would_create
            }
            onClick={
              createSession
            }
          >
            Create Session
          </button>

        </div>


        {
          preview
          && (
            <div
              className={
                preview
                  .would_create
                  ? "rx-onb-admin-preview rx-onb-admin-preview--pass"
                  : "rx-onb-admin-preview rx-onb-admin-preview--fail"
              }
            >

              <div className="rx-onb-admin-preview-title">

                <strong>
                  Assignment Preview
                </strong>

                <Badge
                  status={
                    preview
                      .would_create
                      ? "ready"
                      : "blocked"
                  }
                />

              </div>

              <p>
                {
                  preview
                    .assignment
                    ?.reason
                }
              </p>

              <p>
                Duplicate open session:
                {" "}
                <strong>
                  {
                    preview
                      .open_duplicate_exists
                      ? "YES"
                      : "NO"
                  }
                </strong>
              </p>

            </div>
          )
        }

      </section>


      <section className="rx-onb-admin-card">

        <div className="rx-onb-admin-card-heading">

          <div>
            <h2>
              Onboarding Register
            </h2>

            <p>
              Current sessions and completion progress.
            </p>
          </div>

          <button
            type="button"
            className="rx-onb-admin-link-button"
            onClick={
              reload
            }
          >
            Refresh
          </button>

        </div>


        {
          loading
          ? (
            <div className="rx-onb-admin-empty">
              Loading onboarding register...
            </div>
          )
          : sessions.length
            === 0
          ? (
            <div className="rx-onb-admin-empty">

              <strong>
                No onboarding sessions yet
              </strong>

              <p>
                Create the first controlled
                onboarding session above.
              </p>

            </div>
          )
          : (
            <div className="rx-onb-admin-table-wrap">

              <table className="rx-onb-admin-table">

                <thead>
                  <tr>
                    <th>
                      Worker
                    </th>
                    <th>
                      Pack
                    </th>
                    <th>
                      Status
                    </th>
                    <th>
                      Progress
                    </th>
                    <th>
                      Created
                    </th>
                    <th />
                  </tr>
                </thead>

                <tbody>

                  {
                    sessions.map(
                      (session) => (

                        <tr
                          key={
                            session.id
                            || session._id
                          }
                        >

                          <td>
                            <strong>
                              {
                                session
                                  .subject_snapshot
                                  ?.name
                                || session
                                  .subject_snapshot
                                  ?.email
                                || session
                                  .subject_user_id
                              }
                            </strong>
                          </td>

                          <td>
                            {
                              session
                                .pack_name_snapshot
                            }
                            {" · v"}
                            {
                              session
                                .pack_version
                            }
                          </td>

                          <td>
                            <Badge
                              status={
                                session.status
                              }
                            />
                          </td>

                          <td>
                            <Progress
                              value={
                                session
                                  .completion_percent
                              }
                            />
                          </td>

                          <td>
                            {
                              session.created_at
                                ? new Date(
                                    session.created_at
                                  )
                                  .toLocaleString()
                                : "—"
                            }
                          </td>

                          <td>
                            <button
                              type="button"
                              className="rx-onb-admin-link-button"
                              onClick={
                                () =>
                                  openSession(
                                    session
                                  )
                              }
                            >
                              View
                            </button>
                          </td>

                        </tr>
                      )
                    )
                  }

                </tbody>

              </table>

            </div>
          )
        }

      </section>


      {
        selectedSession
        && (
          <section className="rx-onb-admin-card">

            <div className="rx-onb-admin-card-heading">

              <div>
                <h2>
                  Session Detail
                </h2>
              </div>

              <button
                type="button"
                className="rx-onb-admin-link-button"
                onClick={
                  () =>
                    setSelectedSession(
                      null
                    )
                }
              >
                Close
              </button>

            </div>

            {
              (() => {

                const session =
                  selectedSession
                    .onboarding
                  || selectedSession;

                const events =
                  selectedSession
                    .events
                  || [];

                return (
                  <>

                    <div className="rx-onb-admin-detail-grid">

                      <div>
                        <span>
                          Worker
                        </span>
                        <strong>
                          {
                            session
                              .subject_snapshot
                              ?.name
                            || session
                              .subject_user_id
                          }
                        </strong>
                      </div>

                      <div>
                        <span>
                          Pack
                        </span>
                        <strong>
                          {
                            session
                              .pack_name_snapshot
                          }
                          {" · v"}
                          {
                            session
                              .pack_version
                          }
                        </strong>
                      </div>

                      <div>
                        <span>
                          Progress
                        </span>
                        <strong>
                          {
                            Math.round(
                              session
                                .completion_percent
                              || 0
                            )
                          }%
                        </strong>
                      </div>

                    </div>


                    <h3 className="rx-onb-admin-subheading">
                      Components
                    </h3>

                    <div className="rx-onb-admin-component-grid">

                      {
                        (
                          session
                            .component_progress
                          || []
                        ).map(
                          (
                            component,
                            index
                          ) => (
                            <div
                              className="rx-onb-admin-component"
                              key={
                                component
                                  .component_key
                                || index
                              }
                            >

                              <div>
                                <strong>
                                  {
                                    component
                                      .component_key
                                  }
                                </strong>

                                <small>
                                  {
                                    component
                                      .component_type
                                  }
                                </small>
                              </div>

                              <Badge
                                status={
                                  component
                                    .status
                                }
                              />

                            </div>
                          )
                        )
                      }

                    </div>


                    {
                      events.length > 0
                      && (
                        <>

                          <h3 className="rx-onb-admin-subheading">
                            Timeline
                          </h3>

                          <div className="rx-onb-admin-timeline">

                            {
                              events.map(
                                (
                                  event,
                                  index
                                ) => (
                                  <div
                                    key={
                                      event.id
                                      || index
                                    }
                                  >

                                    <strong>
                                      {
                                        event
                                          .event_type
                                          ?.replaceAll(
                                            "_",
                                            " "
                                          )
                                      }
                                    </strong>

                                    <span>
                                      {
                                        event.timestamp
                                          ? new Date(
                                              event.timestamp
                                            )
                                            .toLocaleString()
                                          : ""
                                      }
                                    </span>

                                  </div>
                                )
                              )
                            }

                          </div>

                        </>
                      )
                    }

                  </>
                );
              })()
            }

          </section>
        )
      }

    </>
  );
}


export default function OnboardingAdmin() {

  const [tab, setTab] =
    useState(
      "register"
    );

  const [packs, setPacks] =
    useState([]);

  const [refs, setRefs] =
    useState({
      workers: [],
      contractors: [],
      sites: [],
      clients: [],
      audience_types: [],
      counts: {},
    });

  const [sessions, setSessions] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");


  const loadAdmin =
    useCallback(
      async () => {

        setLoading(true);
        setError("");

        try {

          const [
            packPayload,
            referencePayload,
            sessionPayload,
          ] = await Promise.all([
            onboardingApi(
              "/admin/packs?status=active"
            ),

            onboardingApi(
              "/admin/reference-data"
            ),

            onboardingApi(
              "/admin/sessions"
            ),
          ]);

          setPacks(
            packPayload.packs
            || []
          );

          setRefs(
            referencePayload
            || {}
          );

          setSessions(
            sessionPayload.sessions
            || []
          );

        } catch (err) {

          setError(
            err.message
          );

        } finally {

          setLoading(false);
        }
      },
      []
    );


  useEffect(
    () => {
      loadAdmin();
    },
    [loadAdmin]
  );


  return (
    <main className="rx-onb-admin">

      <header className="rx-onb-admin-header">

        <div>

          <div className="rx-onb-admin-eyebrow">
            RAPID X COMPLIANCE
          </div>

          <h1>
            Onboarding Administration
          </h1>

          <p>
            Build onboarding packs, assign them
            to staff and contractor workers, and
            monitor completion.
          </p>

        </div>


        <div className="rx-onb-admin-summary">

          <div>
            <strong>
              {
                refs.counts
                  ?.workers
                || 0
              }
            </strong>

            <span>
              Workers
            </span>
          </div>

          <div>
            <strong>
              {packs.length}
            </strong>

            <span>
              Active Packs
            </span>
          </div>

          <div>
            <strong>
              {sessions.length}
            </strong>

            <span>
              Sessions
            </span>
          </div>

        </div>

      </header>


      <div className="rx-onb-tabs">

        <button
          type="button"
          className={
            tab === "register"
              ? "rx-onb-tab rx-onb-tab--active"
              : "rx-onb-tab"
          }
          onClick={
            () =>
              setTab(
                "register"
              )
          }
        >
          Onboarding Register
        </button>

        <button
          type="button"
          className={
            tab === "studio"
              ? "rx-onb-tab rx-onb-tab--active"
              : "rx-onb-tab"
          }
          onClick={
            () =>
              setTab(
                "studio"
              )
          }
        >
          Pack Studio
        </button>

      </div>


      {
        error
        && (
          <div className="rx-onb-admin-alert rx-onb-admin-alert--error">
            {error}
          </div>
        )
      }


      {
        tab === "register"
        ? (
          <AssignmentRegister
            packs={
              packs
            }
            refs={
              refs
            }
            sessions={
              sessions
            }
            loading={
              loading
            }
            reload={
              loadAdmin
            }
          />
        )
        : (
          <PackStudio
            referenceData={
              refs
            }
            onPackActivated={
              loadAdmin
            }
          />
        )
      }

    </main>
  );
}
